import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Box,
  Button,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  IconButton,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { normalizeLang, pickLocalized } from "../../utils/i18nHelpers";
import { userHasActiveEntitlement } from "../../utils/entitlements";

const asArray = (x) => (Array.isArray(x) ? x : []);

const AddRecipeUser = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { user, role, loading: authLoading } = useAuth();

  const lang = normalizeLang(params.lang || i18n.language);

  const isAdmin =
    (typeof role === "string" && role.toLowerCase() === "admin") ||
    (role?.name && role.name.toLowerCase() === "admin");

  // Αν το backend σου έχει άλλο flag, άλλαξε ΜΟΝΟ αυτό:
  const isPremium = userHasActiveEntitlement(user);

  const [pageLoading, setPageLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Lookups
  const [genericProductList, setGenericProductList] = useState([]);
  const [mealTypes, setMealTypes] = useState([]);
  const [foodTypes, setFoodTypes] = useState([]);
  const [dietTypes, setDietTypes] = useState([]);
  const [countryRecipes, setCountryRecipes] = useState([]);
  const [religiousPrefs, setReligiousPrefs] = useState([]);
  const [seasons, setSeasons] = useState([]);

  // Form fields
  const [name, setName] = useState("");
  const [ingredientsText, setIngredientsText] = useState("");
  const [instructions, setInstructions] = useState("");

  const [difficulty, setDifficulty] = useState("");
  const [prepTime, setPrepTime] = useState("");
  const [calories, setCalories] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  const [mealTypeIds, setMealTypeIds] = useState([]);
  const [foodTypeIds, setFoodTypeIds] = useState([]);
  const [dietTypeIds, setDietTypeIds] = useState([]);
  const [religiousPreferenceIds, setReligiousPreferenceIds] = useState([]);

  const [countryRecipesId, setCountryRecipesId] = useState("");
  const [seasonId, setSeasonId] = useState("");

  // Visibility: Free -> private, Premium -> private/pending, Admin -> public allowed
  const [visibility, setVisibility] = useState("private");

  // Products rows
  const [products, setProducts] = useState([
    { generic_product_id: "", quantity: "" },
  ]);

  const canRequestPublic = isPremium || isAdmin;
  const canSetPublic = isAdmin;

  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  // Guard: guest not allowed
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/${lang}/login`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, lang]);

  const uploadImageIfNeeded = async () => {
    // αν δεν διάλεξε ο χρήστης νέο αρχείο, κράτα ότι έχεις ήδη στο imageUrl state
    if (!imageFile) return imageUrl || "";

    const formData = new FormData();
    formData.append("image", imageFile);

    const up = await api.recipes.uploadImage(formData, lang);

    if (up?.data?.success && up?.data?.image_url) {
      // αποθήκευσε το filename και στο state (optional αλλά χρήσιμο)
      setImageUrl(up.data.image_url);
      return up.data.image_url;
    }

    throw new Error(up?.data?.error || "Image upload failed");
  };

  useEffect(() => {
    const loadLookups = async () => {
      setPageLoading(true);
      setErrorMsg("");

      try {
        // Generic Products
        const gpRes = await api.genericProducts.list({ params: { lang } });
        // robust parsing
        const gp = gpRes?.data?.data ?? gpRes?.data ?? [];
        setGenericProductList(asArray(gp));

        // Lookups (προσαρμόζεις αν τα endpoints/return shapes διαφέρουν)
        const [
          mealTypesRes,
          foodTypesRes,
          countryRes,
          dietRes,
          seasonsRes,
          religiousRes,
        ] = await Promise.all([
          api.household.getMealTypes(),
          api.household.getFoodTypes(),
          api.household.getCountryRecipes(),
          api.household.getDietTypes(),
          api.household.getSeasons(),
          api.household.getReligiousPreferences(),
        ]);

        setMealTypes(Array.isArray(mealTypesRes.data) ? mealTypesRes.data : []);
        setFoodTypes(Array.isArray(foodTypesRes.data) ? foodTypesRes.data : []);
        setCountryRecipes(Array.isArray(countryRes.data) ? countryRes.data : []);
        setDietTypes(Array.isArray(dietRes.data) ? dietRes.data : []);
        setSeasons(Array.isArray(seasonsRes.data) ? seasonsRes.data : []);
        const relPayload = religiousRes?.data;
        const relItems = Array.isArray(relPayload?.items)
          ? relPayload.items
          : Array.isArray(relPayload)
            ? relPayload
            : [];
        setReligiousPrefs(relItems);

        // generic products: εδώ έχεις δει ότι μερικές φορές είναι {success,data:[]}
        // const gp = genericProdsRes?.data?.data ?? genericProdsRes?.data ?? [];
        setGenericProductList(Array.isArray(gp) ? gp : []);

      } catch (e) {
        setErrorMsg(
          e?.response?.data?.error ||
          e?.message ||
          t("error_loading_data", "Σφάλμα φόρτωσης δεδομένων.")
        );
      } finally {
        setPageLoading(false);
      }
    };

    loadLookups();
  }, [lang, t]);

  const gpArray = useMemo(
    () => (Array.isArray(genericProductList) ? genericProductList : []),
    [genericProductList]
  );

  const addProductRow = () => {
    setProducts((prev) => [...prev, { generic_product_id: "", quantity: "" }]);
  };

  const removeProductRow = (idx) => {
    setProducts((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateProductRow = (idx, key, value) => {
    setProducts((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
    );
  };

  const validate = () => {
    if (!name.trim()) return t("name_required", "Βάλε όνομα συνταγής.");
    if (!instructions.trim()) return t("instructions_required", "Βάλε οδηγίες.");

    const hasAtLeastOneProduct = products.some(
      (p) => p.generic_product_id && String(p.quantity || "").trim() !== ""
    );
    if (!hasAtLeastOneProduct) {
      return t("products_required", "Πρόσθεσε τουλάχιστον 1 υλικό με ποσότητα.");
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    const vErr = validate();
    if (vErr) {
      setErrorMsg(vErr);
      return;
    }

    setSubmitting(true);
    try {
      // language-specific save (user writes only current language)
      const l = normalizeLang(i18n.language);
      const uploadedImageUrl = await uploadImageIfNeeded();

      // Visibility policy
      let finalVisibility = "private";
      if (canSetPublic && visibility === "public") finalVisibility = "public";
      else if (canRequestPublic && visibility === "pending") finalVisibility = "pending";
      else finalVisibility = "private";

      const payload = {
        [`name_${l}`]: name.trim(),
        [`ingredients_${l}`]: ingredientsText || "",
        [`instructions_${l}`]: instructions || "",

        difficulty: difficulty || null,
        prep_time: prepTime ? Number(prepTime) : null,
        calories: calories ? Number(calories) : null,
        image_url: uploadedImageUrl || "",

        country_recipes_id: countryRecipesId ? Number(countryRecipesId) : null,
        season_id: seasonId ? Number(seasonId) : null,

        meal_type_ids: mealTypeIds.map(Number),
        food_type_ids: foodTypeIds.map(Number),
        diet_type_ids: dietTypeIds.map(Number),

        religious_preference_ids: religiousPreferenceIds.map(Number),

        visibility: finalVisibility,

        products: products
          .filter((p) => p.generic_product_id && p.quantity !== "")
          .map((p) => ({
            generic_product_id: Number(p.generic_product_id),
            quantity: Number(p.quantity),
          })),
      };
      setImageUrl(uploadedImageUrl);

      // Endpoint
      await api.recipes.add(payload);

      navigate(`/${lang}/client/recipes-user`);
    } catch (e2) {
      setErrorMsg(
        e2?.response?.data?.error ||
        e2?.message ||
        t("save_failed", "Αποτυχία αποθήκευσης.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || pageLoading) {
    return (
      <Container sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">
          {t("login_required", "Πρέπει να συνδεθείς για να προσθέσεις συνταγή.")}
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" gutterBottom>
        {t("add_new_recipe", "Προσθήκη νέας συνταγής")}
      </Typography>

      {errorMsg && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMsg}</Alert>
        </Box>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t("recipe_name", "Όνομα")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("ingredients_text", "Υλικά (κείμενο)")}
              value={ingredientsText}
              onChange={(e) => setIngredientsText(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("instructions", "Οδηγίες")}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h6">
              {t("ingredients_structured", "Υλικά (δομημένα)")}
            </Typography>

            {products.map((p, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={gpArray}
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    const label =
                      pickLocalized(option, "name", i18n.language) ||
                      option.name_en ||
                      option.name_el ||
                      option.name ||
                      String(option.id || "");
                    return typeof label === "string" ? label : String(label);
                  }}
                  value={
                    gpArray.find(
                      (gp) => gp?.id?.toString() === p.generic_product_id?.toString()
                    ) || null
                  }
                  onChange={(_, newVal) => {
                    updateProductRow(
                      idx,
                      "generic_product_id",
                      newVal?.id ? String(newVal.id) : ""
                    );
                  }}
                  renderInput={(params2) => (
                    <TextField
                      {...params2}
                      label={t("generic_product", "Γενικό προϊόν")}
                    />
                  )}
                />

                <TextField
                  sx={{ width: 140 }}
                  label={t("quantity_g", "Ποσότητα (g/ml)")}
                  value={p.quantity}
                  onChange={(e) => updateProductRow(idx, "quantity", e.target.value)}
                  type="number"
                />

                <IconButton
                  onClick={() => removeProductRow(idx)}
                  disabled={products.length === 1}
                  aria-label="delete"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addProductRow}
              >
                {t("add_ingredient", "Προσθήκη υλικού")}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t("difficulty.label", "Δυσκολία")}</InputLabel>
              <Select
                label={t("difficulty.label", "Δυσκολία")}
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <MenuItem value="">{t("none", "—")}</MenuItem>
                <MenuItem value="easy">{t("easy", "Εύκολη")}</MenuItem>
                <MenuItem value="medium">{t("medium", "Μέτρια")}</MenuItem>
                <MenuItem value="hard">{t("hard", "Δύσκολη")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t("prep_time_min", "Χρόνος (λεπτά)")}
              value={prepTime}
              onChange={(e) => setPrepTime(e.target.value)}
              fullWidth
              type="number"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t("calories.label", "Θερμίδες")}
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
              fullWidth
              type="number"
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="outlined" component="label" sx={{ mb: 2 }}>
              {t("upload_image", "Μεταφόρτωση φωτογραφίας")}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setImageFile(f);
                  setImagePreviewUrl(f ? URL.createObjectURL(f) : "");
                }}
              />
            </Button>

            {imageFile && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {imageFile.name}
              </Typography>
            )}

            {imagePreviewUrl && (
              <Box sx={{ mb: 2 }}>
                <img
                  src={imagePreviewUrl}
                  alt="preview"
                  style={{
                    width: "100%",
                    maxWidth: "420px",
                    maxHeight: "280px",
                    objectFit: "cover",
                    borderRadius: 12,
                    display: "block",
                  }}
                />
              </Box>
            )}

          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={mealTypes}
              getOptionLabel={(o) => pickLocalized(o, "name", i18n.language)}
              value={mealTypes.filter((m) => mealTypeIds.includes(m.id))}
              onChange={(_, values) => setMealTypeIds(values.map((v) => v.id))}
              renderInput={(p3) => (
                <TextField {...p3} label={t("meal_types", "Γεύματα")} />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={foodTypes}
              getOptionLabel={(o) => pickLocalized(o, "type_name", i18n.language)}
              value={foodTypes.filter((f) => foodTypeIds.includes(f.id))}
              onChange={(_, values) => setFoodTypeIds(values.map((v) => v.id))}
              renderInput={(p3) => (
                <TextField {...p3} label={t("food_types", "Τύποι φαγητού")} />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={dietTypes}
              getOptionLabel={(o) => pickLocalized(o, "diet_name", i18n.language)}
              value={dietTypes.filter((d) => dietTypeIds.includes(d.id))}
              onChange={(_, values) => setDietTypeIds(values.map((v) => v.id))}
              renderInput={(p3) => (
                <TextField {...p3} label={t("diet_types", "Δίαιτες")} />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={religiousPrefs}
              getOptionLabel={(o) =>
                pickLocalized(o, "preference_name", i18n.language)
              }
              value={religiousPrefs.filter((r) => religiousPreferenceIds.includes(r.id))}
              onChange={(_, values) =>
                setReligiousPreferenceIds(values.map((v) => v.id))
              }
              renderInput={(p3) => (
                <TextField {...p3} label={t("religious", "Θρησκευτικά")} />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t("country", "Χώρα κουζίνας")}</InputLabel>
              <Select
                label={t("country", "Χώρα κουζίνας")}
                value={countryRecipesId}
                onChange={(e) => setCountryRecipesId(e.target.value)}
              >
                <MenuItem value="">{t("none", "—")}</MenuItem>
                {countryRecipes.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {pickLocalized(c, "country_name", i18n.language)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t("season", "Εποχή")}</InputLabel>
              <Select
                label={t("season", "Εποχή")}
                value={seasonId}
                onChange={(e) => setSeasonId(e.target.value)}
              >
                <MenuItem value="">{t("none", "—")}</MenuItem>
                {seasons.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {pickLocalized(s, "name", i18n.language)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>{t("visibility", "Ορατότητα")}</InputLabel>
              <Select
                label={t("visibility", "Ορατότητα")}
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
              >
                <MenuItem value="private">{t("private", "Ιδιωτική")}</MenuItem>
                {isPremium && (
                  <MenuItem value="pending">
                    {t("request_publication", "Αίτημα δημοσίευσης")}
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem value="public">{t("public", "Δημόσια")}</MenuItem>
                )}
              </Select>
            </FormControl>

            {!isPremium && !isAdmin && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t(
                  "free_private_only",
                  "Οι free χρήστες μπορούν να αποθηκεύουν μόνο ιδιωτικές συνταγές."
                )}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              fullWidth
            >
              {submitting ? t("saving", "Αποθήκευση...") : t("save", "Αποθήκευση")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AddRecipeUser;
